import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Users } from 'lucide-react';
import { api } from '../lib/api';

type Couple = {
  id: string;
  email: string;
  full_name: string | null;
  phone_primary: string | null;
  is_active: boolean;
  created_at: string;
  events_count: number;
};

export default function PlatformCouplesPage() {
  const [rows, setRows] = useState<Couple[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/platform/couples');
      setRows(r.data);
    } catch {
      toast.error('שגיאה בטעינת זוגות');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSaving(true);
    try {
      await api.post('/platform/couples', {
        email: email.trim(),
        password,
        full_name: fullName.trim() || null,
        event_name: eventName.trim() || undefined,
        event_date: eventDate || undefined,
      });
      toast.success('חשבון הזוג נוצר');
      setEmail('');
      setPassword('');
      setFullName('');
      setEventName('');
      setEventDate('');
      void load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה ביצירה');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c: Couple) {
    try {
      await api.patch(`/platform/couples/${c.id}`, { is_active: !c.is_active });
      toast.success(c.is_active ? 'החשבון הושבת' : 'החשבון הופעל');
      void load();
    } catch {
      toast.error('שגיאה בעדכון');
    }
  }

  return (
    <div className="space-y-8 page-enter">
      <div>
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-sage-600 mb-2">
          Platform
        </p>
        <h1 className="font-sans text-4xl font-bold text-ink tracking-tight">זוגות</h1>
        <p className="text-ink-mute mt-1.5 text-[15px]">
          יצירת חשבונות זוג ואירוע ראשון — הזוג רואה רק את החתונות שלו
        </p>
      </div>

      <form onSubmit={create} className="surface p-5 sm:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Plus size={16} className="text-sage-600" />
          <h2 className="font-semibold text-ink">זוג חדש</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="c-email">אימייל</label>
            <input
              id="c-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field"
              required
              dir="ltr"
            />
          </div>
          <div>
            <label className="label" htmlFor="c-pass">סיסמה</label>
            <input
              id="c-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field"
              required
              minLength={6}
              dir="ltr"
            />
          </div>
          <div>
            <label className="label" htmlFor="c-name">שם הזוג</label>
            <input
              id="c-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="field"
              placeholder="דנה ויוסי"
            />
          </div>
          <div className="hidden sm:block" />
          <div>
            <label className="label" htmlFor="c-ev">אירוע ראשון (אופציונלי)</label>
            <input
              id="c-ev"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="field"
              placeholder="שם החתונה"
            />
          </div>
          <div>
            <label className="label" htmlFor="c-date">תאריך אירוע</label>
            <input
              id="c-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="field"
            />
          </div>
        </div>
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? 'יוצר…' : 'יצירת חשבון'}
        </button>
      </form>

      <div className="surface overflow-hidden divide-y divide-line">
        {loading ? (
          <div className="p-8 text-center text-ink-faint text-sm">טוען…</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Users className="mx-auto text-sage-600 mb-3" size={28} />
            <p className="text-ink-mute">אין זוגות עדיין</p>
          </div>
        ) : (
          rows.map((c) => (
            <div key={c.id} className="px-4 py-3.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-ink truncate">
                  {c.full_name || c.email}
                </div>
                <div className="text-[12px] text-ink-faint mt-0.5" dir="ltr">
                  {c.email}
                  {c.full_name ? ` · ${c.events_count} אירועים` : ` · ${c.events_count} אירועים`}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`text-[11px] font-medium px-2 py-1 rounded-full ${
                    c.is_active ? 'bg-sage-50 text-sage-700' : 'bg-red-50 text-red-600'
                  }`}
                >
                  {c.is_active ? 'פעיל' : 'מושבת'}
                </span>
                <button type="button" className="btn-ghost !px-3 !py-1.5 text-[12px]" onClick={() => void toggleActive(c)}>
                  {c.is_active ? 'השבת' : 'הפעל'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
